/**
 * HubTenantsPage — HUB-REDESIGN P3.
 *
 * Dense list reshelled from the design handoff at
 * /Users/joemeadows/Desktop/design_handoff_tenon_hub/tenants.jsx onto the
 * shared Card/KPI/Pill/Endpoint atoms. Wires to the existing
 * /hub/tenants endpoint (list + summary). Functionality preserved from
 * the prior card-grid implementation: status filter chips, search, the
 * admin-create modal (HUB-12-6), refresh, navigation to /tenants/:id.
 */
import { useEffect, useMemo, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Endpoint, KPI, Pill, StatusDot, Icon } from './components'
import { hubTenants, type HubTenantsResponse, type HubTenantSummary } from './api'

type StatusFilter = 'all' | 'active' | 'trialing' | 'past_due' | 'sandbox'

function statusPillVariant(status: string): 'live' | 'info' | 'err' | 'warn' | 'ghost' {
  if (status === 'active') return 'live'
  if (status === 'trialing') return 'info'
  if (status === 'past_due' || status === 'expired' || status === 'canceled' || status === 'cancelled') return 'err'
  if (status === 'suspended') return 'warn'
  return 'ghost'
}

function healthDotTone(t: HubTenantSummary): 'ok' | 'warn' | 'err' | 'info' | 'dim' {
  if (t.subscription_status === 'past_due' || t.subscription_status === 'suspended') return 'err'
  if (t.flags.length > 0) return 'warn'
  if (t.subscription_status === 'active') return 'ok'
  if (t.subscription_status === 'trialing') return 'info'
  return 'dim'
}

function formatMRR(cents: number): string {
  return cents > 0 ? `$${(cents / 100).toFixed(0)}` : '—'
}

function formatLastSeen(dateStr: string | null): { text: string; tone: 'ok' | 'warn' | 'err' | 'dim' } {
  if (!dateStr) return { text: 'never', tone: 'err' }
  const diff = Date.now() - new Date(dateStr).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return { text: 'just now', tone: 'ok' }
  if (m < 60) return { text: `${m}m`, tone: 'ok' }
  const h = Math.floor(m / 60)
  if (h < 24) return { text: `${h}h`, tone: 'ok' }
  const d = Math.floor(h / 24)
  if (d < 7) return { text: `${d}d`, tone: 'warn' }
  return { text: `${d}d`, tone: 'dim' }
}

export default function HubTenantsPage() {
  const [data, setData] = useState<HubTenantsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [filter, setFilter] = useState<StatusFilter>('all')
  const [createOpen, setCreateOpen] = useState(false)
  const navigate = useNavigate()

  const refresh = useCallback(async () => {
    try {
      const d = await hubTenants.list()
      setData(d)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenants')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
    const id = setInterval(refresh, 60_000)
    return () => clearInterval(id)
  }, [refresh])

  const counts = useMemo(() => {
    if (!data) return { all: 0, active: 0, trialing: 0, past_due: 0, sandbox: 0 }
    return {
      all: data.summary.total,
      active: data.summary.active,
      trialing: data.summary.trialing,
      past_due: data.summary.past_due,
      sandbox: data.tenants.filter(t => t.is_sandbox).length,
    }
  }, [data])

  const filtered = useMemo(() => {
    if (!data) return []
    return data.tenants.filter(t => {
      if (filter === 'sandbox' && !t.is_sandbox) return false
      if (filter !== 'all' && filter !== 'sandbox' && t.subscription_status !== filter) return false
      if (q && !t.company_name.toLowerCase().includes(q.toLowerCase())) return false
      return true
    })
  }, [data, filter, q])

  if (loading && !data) {
    return <div style={{ padding: 14, color: 'var(--text-muted)', fontSize: 13 }}>Loading tenants…</div>
  }
  if (error && !data) {
    return <div style={{ padding: 14, color: 'var(--err)', fontSize: 13 }}>{error}</div>
  }
  if (!data) return null

  const { summary } = data
  const trialingEndingSoon = data.tenants.filter(t => {
    if (t.subscription_status !== 'trialing' || !t.trial_ends_at) return false
    const days = (new Date(t.trial_ends_at).getTime() - Date.now()) / 86400000
    return days >= 0 && days <= 7
  }).length

  return (
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0 }}>
      {/* KPI strip */}
      <Card>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, background: 'var(--border)' }}>
          <div style={{ background: 'var(--surface)' }}>
            <KPI label="Total tenants" value={summary.total} delta={summary.active > 0 ? `${summary.active} active` : ''} tone="flat" endpoint="GET /hub/tenants" />
          </div>
          <div style={{ background: 'var(--surface)' }}>
            <KPI label="Paying" value={summary.active} delta={`$${(summary.mrr_cents / 100).toFixed(0)} MRR`} tone={summary.mrr_cents > 0 ? 'up' : 'flat'} />
          </div>
          <div style={{ background: 'var(--surface)' }}>
            <KPI label="Trialing" value={summary.trialing} delta={trialingEndingSoon > 0 ? `${trialingEndingSoon} ending ≤7d` : ''} tone={trialingEndingSoon > 0 ? 'down' : 'flat'} />
          </div>
          <div style={{ background: 'var(--surface)' }}>
            <KPI label="At-risk" value={summary.at_risk_count} delta={summary.past_due > 0 ? `${summary.past_due} past due` : 'last 7d'} tone={summary.at_risk_count > 0 ? 'down' : 'flat'} />
          </div>
          <div style={{ background: 'var(--surface)' }}>
            <KPI label="Sandbox" value={counts.sandbox} delta={counts.sandbox > 0 ? data.tenants.find(t => t.is_sandbox)?.company_name?.split(' ')[0] : ''} tone="flat" />
          </div>
        </div>
      </Card>

      {/* Toolbar */}
      <div className="row" style={{ gap: 8 }}>
        <div className="row" style={{ gap: 4, flex: 1, flexWrap: 'wrap' }}>
          {([
            { k: 'all', l: 'All' },
            { k: 'active', l: 'Active' },
            { k: 'trialing', l: 'Trialing' },
            { k: 'past_due', l: 'Past due' },
            { k: 'sandbox', l: 'Sandbox' },
          ] as const).map(f => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              className="btn"
              style={{
                fontSize: 11.5, height: 26, padding: '0 10px',
                background: filter === f.k ? 'var(--accent-soft)' : 'var(--surface)',
                borderColor: filter === f.k ? 'var(--accent-ring)' : 'var(--border-strong)',
                color: filter === f.k ? 'var(--accent)' : 'var(--text-muted)',
              }}
            >
              {f.l} <span className="mono" style={{ opacity: 0.7, marginLeft: 4 }}>{counts[f.k]}</span>
            </button>
          ))}
        </div>
        <div className="row" style={{ gap: 6, position: 'relative' }}>
          <Icon name="search" size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)', pointerEvents: 'none' }} />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search tenants…"
            style={{
              background: 'var(--surface)', border: '1px solid var(--border-strong)',
              borderRadius: 5, color: 'var(--text)', height: 26,
              padding: '0 8px 0 26px', fontSize: 12, fontFamily: 'inherit', width: 200, outline: 'none',
            }}
          />
        </div>
        <button className="btn" onClick={refresh} title="Refresh">Refresh</button>
        <button className="btn primary" onClick={() => setCreateOpen(true)}><Icon name="plus" size={11} /> Invite tenant</button>
      </div>

      {/* Table */}
      <Card>
        <div className="head">
          <span className="title">{filtered.length} tenants</span>
          <span className="grow" />
          <Endpoint>GET /hub/tenants</Endpoint>
        </div>
        {filtered.length === 0 ? (
          <div className="body" style={{ color: 'var(--text-dim)', fontSize: 12, fontStyle: 'italic' }}>
            No tenants match the current filter.
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 14 }}></th>
                <th>Name</th>
                <th>Status</th>
                <th>Plan</th>
                <th style={{ textAlign: 'right' }}>Users</th>
                <th style={{ textAlign: 'right' }}>Projects</th>
                <th style={{ textAlign: 'right' }}>MRR</th>
                <th>Last seen</th>
                <th>AI 30d</th>
                <th style={{ width: 28 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => {
                const last = formatLastSeen(t.last_seen_at)
                return (
                  <tr key={t.company_id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/tenants/${t.company_id}`)}>
                    <td><StatusDot tone={healthDotTone(t)} /></td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontWeight: 500 }}>{t.company_name}</span>
                        {t.is_sandbox && <Pill variant="info">Sandbox</Pill>}
                      </div>
                      <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-dim)', marginTop: 2 }}>
                        ID #{t.company_id}
                      </div>
                    </td>
                    <td><Pill variant={statusPillVariant(t.subscription_status)}>{t.subscription_status}</Pill></td>
                    <td>
                      <span className="mono" style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{t.plan ?? '—'}</span>
                    </td>
                    <td style={{ textAlign: 'right' }} className="mono">{t.user_count}</td>
                    <td style={{ textAlign: 'right' }} className="mono">{t.project_count}</td>
                    <td style={{ textAlign: 'right', color: t.mrr_cents > 0 ? 'var(--text)' : 'var(--text-dim)' }} className="mono">{formatMRR(t.mrr_cents)}</td>
                    <td className="mono" style={{ fontSize: 11.5, color: last.tone === 'err' ? 'var(--err)' : last.tone === 'warn' ? 'var(--warn)' : last.tone === 'ok' ? 'var(--text-muted)' : 'var(--text-dim)' }}>{last.text}</td>
                    <td className="mono" style={{ fontSize: 11, color: 'var(--text-dim)' }}>{Number(t.ai_tokens_30d || 0).toLocaleString()}</td>
                    <td><Icon name="chevron-right" size={12} style={{ color: 'var(--text-dim)' }} /></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </Card>

      {createOpen && (
        <CreateTenantModal
          onCancel={() => setCreateOpen(false)}
          onCreated={(companyID) => {
            setCreateOpen(false)
            refresh()
            navigate(`/tenants/${companyID}`)
          }}
        />
      )}
    </div>
  )
}

function CreateTenantModal({ onCancel, onCreated }: {
  onCancel: () => void
  onCreated: (companyID: number) => void
}) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [ownerName, setOwnerName] = useState('')
  const [plan, setPlan] = useState('starter')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [created, setCreated] = useState<{ company_id: number; temp_password: string; owner_email: string } | null>(null)

  const submit = async () => {
    if (busy) return
    setErr(null)
    setBusy(true)
    try {
      const res = await hubTenants.adminCreate({
        company_name: name.trim(),
        owner_email: email.trim(),
        owner_name: ownerName.trim(),
        plan: plan.trim() || 'starter',
      })
      setCreated({ company_id: res.company_id, temp_password: res.temp_password, owner_email: res.owner_email })
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Create failed')
    } finally {
      setBusy(false)
    }
  }

  const close = () => {
    if (created) onCreated(created.company_id)
    else onCancel()
  }

  return (
    <div
      onClick={close}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
      }}
    >
      <div onClick={e => e.stopPropagation()} className="card" style={{ width: '100%', maxWidth: 480 }}>
        <div className="head">
          <span className="title">{created ? 'Tenant created' : 'Invite tenant (admin signup-bypass)'}</span>
          <span className="grow" />
          <Endpoint>POST /hub/tenants/admin-create</Endpoint>
        </div>
        <div className="body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <p style={{ fontSize: 12, color: 'var(--text-dim)', margin: 0 }}>
            {created
              ? 'Share the temporary password with the operator out-of-band — it is shown only once.'
              : 'For sales-driven onboarding only. Skips email verification + payment capture.'}
          </p>

          {err && (
            <div className="alert-chip err" style={{ padding: '6px 10px' }}>{err}</div>
          )}

          {created ? (
            <>
              <ModalField label="Company ID" value={`#${created.company_id}`} mono />
              <ModalField label="Owner email" value={created.owner_email} mono />
              <ModalField label="Temp password" value={created.temp_password} mono />
              <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: 0 }}>
                The user must change this on first login. Store it securely.
              </p>
            </>
          ) : (
            <>
              <ModalInput label="Company name" value={name} onChange={setName} placeholder="Apex Construction Ltd." />
              <ModalInput label="Owner email" value={email} onChange={setEmail} placeholder="ops@apex.example" type="email" />
              <ModalInput label="Owner name" value={ownerName} onChange={setOwnerName} placeholder="Pat Owens" />
              <ModalInput label="Plan" value={plan} onChange={setPlan} placeholder="starter" />
            </>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            {!created && (
              <button className="btn ghost" onClick={onCancel}>Cancel</button>
            )}
            <button
              className="btn primary"
              onClick={created ? close : submit}
              disabled={busy || (!created && (!name.trim() || !email.trim()))}
              style={{ opacity: busy || (!created && (!name.trim() || !email.trim())) ? 0.5 : 1 }}
            >
              {busy ? 'Working…' : created ? 'Open tenant' : 'Create tenant'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function ModalInput({ label, value, onChange, placeholder, type = 'text' }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string
}) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{
        fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase',
        letterSpacing: '0.08em', fontWeight: 600,
      }}>
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          marginTop: 4, width: '100%', padding: '6px 10px', borderRadius: 5,
          background: 'var(--surface-2)', color: 'var(--text)',
          border: '1px solid var(--border-strong)', fontFamily: 'inherit', fontSize: 12,
          outline: 'none',
        }}
      />
    </label>
  )
}

function ModalField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>{label}</div>
      <div className={mono ? 'mono' : ''} style={{
        fontSize: 13, color: 'var(--text)', marginTop: 2, wordBreak: 'break-all',
      }}>
        {value}
      </div>
    </div>
  )
}
